import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Form,
  Button,
  Input,
  InputNumber,
  message,
  Select,
  Table,
  Switch,
  Modal,
  Tag,
  Radio,
} from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const TYPE_OPTIONS = [{ value: "VOUCHER", label: "Voucher" }];
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const RewardCatalog = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 });
  const [items, setItems] = useState([]);
  const [minRedemption, setMinRedemption] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchItems = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));
    try {
      const params = {
        limit: pagination.limit,
        page: pagination.currentPage,
        ...(showArchived && { archived: true }),
      };
      const res = await axios.get("/api/reward-items", { params });
      setItems(res.data.data);
      setPagination((prev) => ({
        ...prev,
        totalSize: res.data.pagination?.totalSize,
      }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, showArchived]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Min redemption gates whether a reward can ever be redeemed, so the catalog form rejects a
  // point cost below it (mirrors the backend RewardItemController check).
  useEffect(() => {
    axios
      .get("/api/redemption-settings")
      .then((res) => setMinRedemption(res.data.data?.minRedemption ?? null))
      .catch(() => setMinRedemption(null));
  }, []);

  const pointCostRules = [
    { required: true, type: "number", min: 1 },
    {
      validator: (_, value) =>
        value == null ||
        minRedemption == null ||
        value >= minRedemption
          ? Promise.resolve()
          : Promise.reject(
              new Error(`Point cost must be at least ${minRedemption} points`),
            ),
    },
  ];

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const data = {
        name: values.name,
        description: values.description,
        image: values.image,
        type: values.type,
        pointCost: values.pointCost,
        stock: values.stock ?? null,
        isActive: values.isActive ?? true,
        fulfillmentConfig: {
          rebateAmount: values.rebateAmount,
          minOrderValue: values.minOrderValue,
          validityDays: values.validityDays,
        },
      };
      await axios.post("/api/reward-items", data);
      message.success("Reward item created");
      form.resetFields();
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const updateHandler = async (itemId, values) => {
    setLoading((prev) => ({ ...prev, update: true }));
    try {
      // Explicit build mirrors createHandler — no spread-then-delete fragility.
      // `type` is intentionally omitted: the backend resolves it from the stored record,
      // so sending a stale client-side value is risky and redundant.
      const data = {
        name: values.name,
        description: values.description,
        image: values.image,
        pointCost: values.pointCost,
        stock: values.stock ?? null,   // undefined (cleared) → null = unlimited
        isActive: values.isActive,
        fulfillmentConfig: {
          rebateAmount: values.rebateAmount,
          minOrderValue: values.minOrderValue,
          validityDays: values.validityDays,
        },
      };
      await axios.patch(`/api/reward-items/${itemId}`, data);
      message.success("Reward item updated");
      setEditingItem(null);
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const toggleStatusHandler = async (itemId, isActive) => {
    try {
      await axios.patch(`/api/reward-items/${itemId}/toggle-status`, { isActive });
      message.success("Status updated");
      fetchItems();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const archiveHandler = (itemId) => {
    Modal.confirm({
      title: "Archive reward item",
      content: "Customers will no longer see this item in the catalog.",
      async onOk() {
        try {
          await axios.patch(`/api/reward-items/${itemId}/archive`);
          message.success("Item archived");
          fetchItems();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
          throw error; // reject so AntD keeps the dialog open and shows the failure
        }
      },
    });
  };

  const unarchiveHandler = (itemId) => {
    Modal.confirm({
      title: "Restore reward item",
      content: "The item will be moved back to the active catalog list.",
      async onOk() {
        try {
          await axios.patch(`/api/reward-items/${itemId}/unarchive`);
          message.success("Item restored");
          fetchItems();
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
          throw error;
        }
      },
    });
  };

  const handleArchivedToggle = (e) => {
    setShowArchived(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    const config = record.fulfillmentConfig || {};
    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      image: record.image,
      type: record.type,
      pointCost: record.pointCost,
      stock: record.stock,
      isActive: record.isActive,
      rebateAmount: config.rebateAmount,
      minOrderValue: config.minOrderValue,
      validityDays: config.validityDays,
    });
  };

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", width: 200 },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 100,
      render: (t) => <Tag>{t}</Tag>,
    },
    { title: "Point cost", dataIndex: "pointCost", key: "pointCost", width: 120 },
    {
      title: "Rebate",
      key: "rebate",
      width: 110,
      render: (_, r) =>
        r.fulfillmentConfig?.rebateAmount
          ? `RM${r.fulfillmentConfig.rebateAmount} (min RM${r.fulfillmentConfig.minOrderValue})`
          : "-",
    },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
      width: 90,
      render: (v) => (v === null || v === undefined ? "∞" : v),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 90,
      render: (value, record) =>
        !showArchived &&
        permissions?.includes(PERMISSIONS.TOGGLE_REWARD_ITEM_STATUS) ? (
          <Switch
            checked={value}
            onChange={(checked) => toggleStatusHandler(record.objectId, checked)}
          />
        ) : value ? "Yes" : "No",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => (text ? formatDateTime(text) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) =>
        showArchived ? (
          permissions?.includes(PERMISSIONS.DELETE_REWARD_ITEM) && (
            <Button type="link" onClick={() => unarchiveHandler(record.objectId)}>
              Restore
            </Button>
          )
        ) : (
          <>
            {permissions?.includes(PERMISSIONS.UPDATE_REWARD_ITEM) && (
              <Button type="link" onClick={() => handleEdit(record)}>Edit</Button>
            )}
            {permissions?.includes(PERMISSIONS.DELETE_REWARD_ITEM) && (
              <Button type="link" danger onClick={() => archiveHandler(record.objectId)}>
                Archive
              </Button>
            )}
          </>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Reward Catalog"
        childRoutes={[{ path: "/reward-catalog", breadcrumbName: "Reward Catalog" }]}
      />

      {permissions?.includes(PERMISSIONS.CREATE_REWARD_ITEM) && (
        <Collapse
          expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
          style={{ marginBottom: 16 }}
        >
          <Collapse.Panel header="Create new reward item" key="create">
            <Form
              form={form}
              layout="vertical"
              onFinish={createHandler}
              initialValues={{ isActive: true, type: "VOUCHER" }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                    <Select>
                      {TYPE_OPTIONS.map((t) => (
                        <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="pointCost"
                    label="Point cost"
                    rules={pointCostRules}
                  >
                    <InputNumber min={1} precision={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="rebateAmount" label="Rebate amount (RM)" rules={[{ required: true, type: "number", min: 0.01 }]}>
                    <InputNumber min={0.01} step={0.5} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="minOrderValue"
                    label="Min order value (RM)"
                    rules={[
                      {
                        required: true,
                        type: "number",
                        min: 0.01,
                        message: "Min order value must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber min={0.01} step={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="validityDays" label="Validity (days)" rules={[{ required: true, type: "number", min: 1 }]}>
                    <InputNumber min={1} precision={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="stock"
                    label="Stock (blank = unlimited)"
                    rules={[{ type: "number", min: 0 }]}
                  >
                    <InputNumber min={0} precision={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item name="isActive" label="Active" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="image" label="Image URL">
                    <Input placeholder="https://..." />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="description" label="Description">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </Col>
              </Row>
              <Button type="primary" htmlType="submit" loading={loading.create}>
                Create item
              </Button>
            </Form>
          </Collapse.Panel>
        </Collapse>
      )}

      <Row justify="start" style={{ marginTop: 16, marginBottom: 8 }}>
        <Col>
          <Radio.Group
            value={showArchived}
            onChange={handleArchivedToggle}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value={false}>Active</Radio.Button>
            <Radio.Button value={true}>Archived</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading.index}
        scroll={{ x: true }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>

      <Modal
        title="Edit reward item"
        visible={!!editingItem}
        onCancel={() => setEditingItem(null)}
        onOk={() => {
          // Capture id before the async gap — modal may be dismissed between
          // validateFields() and the .then(), clearing editingItem to null.
          const item = editingItem;
          editForm.validateFields()
            .then((values) => { if (item) updateHandler(item.objectId, values); })
            .catch(() => {});  // validation errors are shown inline; suppress unhandled rejection
        }}
        confirmLoading={loading.update}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="Type">
            <Tag color="blue">{editingItem?.type}</Tag>
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="image" label="Image URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="pointCost" label="Point cost" rules={pointCostRules}>
            <InputNumber min={1} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="rebateAmount" label="Rebate amount (RM)" rules={[{ required: true, type: "number", min: 0.01 }]}>
            <InputNumber min={0.01} step={0.5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="minOrderValue"
            label="Min order value (RM)"
            rules={[
              {
                required: true,
                type: "number",
                min: 0.01,
                message: "Min order value must be greater than 0",
              },
            ]}
          >
            <InputNumber min={0.01} step={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="validityDays" label="Validity (days)" rules={[{ required: true, type: "number", min: 1 }]}>
            <InputNumber min={1} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="stock"
            label="Stock (blank = unlimited)"
            rules={[{ type: "number", min: 0 }]}
          >
            <InputNumber min={0} precision={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RewardCatalog;
